import {DragEvent, useLayoutEffect, useRef} from 'react'
import onResize from 'simple-element-resize-detector'
import {HereMapAtomProps} from '../@atoms/to_content/map_h/here_map.atom'
import map_marker from '../assets/images/marker.png'
import {RootState} from '../store_redux'
import { useSelector } from 'react-redux'

const useHereMap = (props: HereMapAtomProps) => {
  // Create a reference to the HTML element we want to put the map on
  const mapRef = useRef(null)
  const rootState = useSelector((state: RootState) => state)
  const {labels} = rootState
  const {lat, lng, isCoordinateFromPostCodeLookup, api_key, app_id, ap_position, setAddressBasedOnGeoCode} =
    props
  const {H} = window

  function CreateHERETextIcon(textString: string, colour: string): HTMLDivElement {
    var outerElement = document.createElement('div'),
    innerElement = document.createElement('div');

    outerElement.style.userSelect = 'none';
    outerElement.style.cursor = 'default';

    // Set text colour to our passed in colour
    innerElement.style.color = colour;
    // Set background color to transparent
    innerElement.style.backgroundColor = 'transparent';
    innerElement.style.font = 'italic 16px Roboto';

    // Keep text on a single line
    innerElement.style.whiteSpace = 'nowrap';

    // Make the centre of the text the location of the icon
    innerElement.style.position = 'absolute';
    innerElement.style.top = '50%';
    innerElement.style.left = '50%';
    innerElement.style.transform = 'translate(-50%, -50%)';
    
    outerElement.appendChild(innerElement);

    // Add text passed in to the DOM element
    innerElement.innerHTML = textString;
  
    // Return our new DOMIcon
    return new H.map.DomIcon(outerElement);
  }

  useLayoutEffect(
    () => {
      // `mapRef.current` will be `undefined` when this hook first runs; edge case that
      if (!mapRef.current) return

      // initialize communication with the platform
      const platform = new H.service.Platform({
        apikey: api_key,
        app_id,
      })
      const defaultLayers = platform.createDefaultLayers()

      // initialize a map
      const map = new H.Map(mapRef.current, defaultLayers.vector.normal.map, {
        center: {lat, lng},
        zoom: 16,
        pixelRatio: window.devicePixelRatio || 1,
      })

      // add a resize listener to make sure that the map occupies the whole container
      onResize(mapRef.current, function () {
        map.getViewPort().resize()
      })

      // MapEvents enables the event system
      // Behavior implements default interactions for pan/zoom (also on mobile touch environments)
      const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map))

      // Create the default UI:
      H.ui.UI.createDefault(map, defaultLayers)
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let marker: any
      if (isCoordinateFromPostCodeLookup === false) {
        // The coordinate is an exact point based upon the full address

        // Add a draggable or non-draggable marker to the map
        const iconMarker = new H.map.Icon(`${map_marker}`, {
          size: {w: 22, h: 32},
          position: 'relative',
          margin: {bottom: 20},
        })

        marker = new H.map.Marker(
          {lat, lng},
          {
            icon: iconMarker,
            // mark the object as volatile for the smooth dragging
            volatility: true,
          },
        )

        marker.draggable = ap_position === 'EDIT_LOCATION_SCREEN'
        map.addObject(marker)

      } else {

        // The coordinate is an approximate location based upon the postcode only
        const circle = new H.map.Circle(
          // The central point of the circle
          {lat, lng},
          // The radius of the circle in meters
          200,
          {
            style: {
              strokeColor: 'rgba(0, 0, 0, 0.0)', // Colour of the perimeter
              lineWidth: 2,
              fillColor: 'rgba(127, 94, 147, 0.4)'  // Colour of the circle
            }
          }
        )

        // Add the circle to the map
        map.addObject(circle)

        // Create some text in the circle which tells us this is the approximate location
        const textIcon = CreateHERETextIcon(labels.hereMapDisplay.approximateShippingAddress, '#444e5a');
        var textMarker = new H.map.DomMarker({lat, lng}, {
          icon: textIcon
        });

        // Add the 'Approximate location' text marker to the circle/map
        map.addObject(textMarker);

      }

      // Add drag event for draggable marker
      if (ap_position === 'EDIT_LOCATION_SCREEN') {
        // disable the default draggability of the underlying map
        // and calculate the offset between mouse and target's position
        // when starting to drag a marker object:
        const dragStartEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          const pointer = ev.currentPointer
          if (target instanceof H.map.Marker) {
            const targetPosition = map.geoToScreen(target.getGeometry())
            target.offset = new H.math.Point(
              pointer.viewportX - targetPosition.x,
              pointer.viewportY - targetPosition.y,
            )
            behavior.disable()
          }
        }

        map.addEventListener('dragstart', dragStartEventHandler, false)

        // re-enable the default draggability of the underlying map
        // when dragging has completed
        const dragEndEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          if (target instanceof H.map.Marker) {
            const geometry = marker.getGeometry()
            map.setCenter({lat: geometry.lat, lng: geometry.lng})
            behavior.enable()
            if (setAddressBasedOnGeoCode)
              setAddressBasedOnGeoCode(geometry.lat, geometry.lng).catch(() => {
                marker.setGeometry({lat, lng})
                map.setCenter({lat, lng})
              })
          }
        }

        map.addEventListener('dragend', dragEndEventHandler, false)

        // Listen to the drag event and move the position of the marker
        // as necessary
        const dragEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          const pointer = ev.currentPointer
          if (target instanceof H.map.Marker) {
            target.setGeometry(
              map.screenToGeo(
                pointer.viewportX - target.offset.x,
                pointer.viewportY - target.offset.y,
              ),
            )
          }
        }

        map.addEventListener('drag', dragEventHandler, false)
      }

      /*eslint-disable */
      return () => {
        map.dispose()
      }
      /* eslint-enable */
    }, // eslint-disable-next-line
    [
      api_key,
      app_id,
      lat,
      lng,
      ap_position,
      mapRef,
      H.Map,
      H.map.Icon,
      H.map.Marker,
      H.mapevents.Behavior,
      H.mapevents.MapEvents,
      H.service.Platform,
      H.ui.UI,
    ],
  )

  return mapRef
}

export default useHereMap
